// Окно «Отдать на биржу» — звонильщик заводит заявку прямо из карточки клиента.
// Макет утверждён Антоном 30.07 (preview/_birzha-preview.html).
//
// Три вещи, которые здесь важны и проверялись на макете:
//   1) публикация убыточной заявки не проходит (преподу больше, чем платит клиент);
//   2) справа живой предпросмотр — карточка ГЛАЗАМИ ПРЕПОДАВАТЕЛЯ;
//   3) до публикации видно, скольким уйдёт заявка.
// Контакты ученика преподавателю не показываются — ни здесь, ни в карточке.
import { useState, useEffect, useMemo, useCallback } from 'react'
import api from '../api'

const SUBJECTS = ['Математика', 'Русский язык', 'Английский язык', 'Физика', 'Химия', 'Биология',
  'Информатика', 'История', 'Обществознание', 'География', 'Окружающий мир', 'Немецкий язык', 'Психология']
const GRADES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', 'ОГЭ', 'ЕГЭ']

const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
const WEEKDAYS = ['в воскресенье', 'в понедельник', 'во вторник', 'в среду',
  'в четверг', 'в пятницу', 'в субботу']

/** «2026-08-06» → «в четверг, 6 августа». Пустая строка, если дата не выбрана. */
function humanDate(iso) {
  if (!iso) return ''
  const d = new Date(iso + 'T12:00:00')
  if (Number.isNaN(d.getTime())) return ''
  return `${WEEKDAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]}`
}

/** «17:00» + 55 минут → «17:55». Та же длительность, что на платформе. */
function plus55(time) {
  const [h, m] = String(time || '').split(':').map(Number)
  if (!Number.isFinite(h) || !Number.isFinite(m)) return ''
  const total = h * 60 + m + 55
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

/** Класс в заголовок карточки: «8 класс», но «ОГЭ»/«ЕГЭ» — без слова «класс». */
function gradeLabel(grade) {
  const g = String(grade || '').trim()
  if (!g) return ''
  return /^\d+$/.test(g) ? `${g} класс` : g
}

/** Предмет из анкеты лида → наш список. «Матем» → «Математика». */
function guessSubject(raw) {
  const s = String(raw || '').toLowerCase()
  if (!s) return ''
  return SUBJECTS.find(x => s.includes(x.toLowerCase().split(' ')[0].slice(0, 5))) || ''
}

/** Класс из анкеты: «8 класс» → «8», «егэ» → «ЕГЭ». */
function guessGrade(raw) {
  const s = String(raw || '').trim().toLowerCase()
  if (!s) return ''
  if (s.includes('огэ')) return 'ОГЭ'
  if (s.includes('егэ')) return 'ЕГЭ'
  const m = s.match(/(\d{1,2})/)
  return m && GRADES.includes(m[1]) ? m[1] : ''
}

export default function BirzhaModal({ open, onClose, clientLabel, dealId, initial = {}, onCreated }) {
  const [subject, setSubject] = useState('')
  const [grade, setGrade] = useState('')
  const [date, setDate] = useState('')
  const [time, setTime] = useState('17:00')
  const [prefer, setPrefer] = useState('')
  const [note, setNote] = useState('')
  const [clientPrice, setClientPrice] = useState(400)
  const [teacherPrice, setTeacherPrice] = useState(300)
  const [audience, setAudience] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(null)

  // Подставляем то, что уже известно из сделки, — менеджеру остаётся дата и время.
  useEffect(() => {
    if (!open) return
    setSubject(guessSubject(initial.subject))
    setGrade(guessGrade(initial.classNum))
    setDate(''); setTime('17:00'); setPrefer(''); setNote('')
    setClientPrice(400); setTeacherPrice(300)
    setError(''); setDone(null); setAudience(null)
  }, [open, initial.subject, initial.classNum])

  // Сколько преподавателей получит заявку по этому предмету.
  useEffect(() => {
    if (!open || !subject) { setAudience(null); return }
    let alive = true
    api.get('/api/exchange/audience', { params: { subject } })
      .then(r => { if (alive) setAudience(r.data) })
      .catch(() => { if (alive) setAudience(null) })
    return () => { alive = false }
  }, [open, subject])

  const margin = useMemo(
    () => Number(clientPrice || 0) - Number(teacherPrice || 0),
    [clientPrice, teacherPrice])
  const marginBad = margin < 0

  const canSubmit = Boolean(subject && grade && date && time) && !marginBad && !busy

  const submit = useCallback(async () => {
    setBusy(true); setError('')
    try {
      const r = await api.post('/api/exchange/offers', {
        subject, grade, slot_date: date, slot_begin_time: time, slot_end_time: plus55(time),
        prefer_text: prefer.trim() || null,
        note: note.trim() || null,
        client_price: Number(clientPrice), teacher_price: Number(teacherPrice),
        student_mk_id: initial.mkId || null,
        student_name: initial.childName || null,
        // Сделка после публикации переезжает в колонку «БИРЖА» — бэк двигает её в AmoCRM.
        lead_id: dealId || null,
      })
      setDone(r.data)
      if (onCreated) onCreated(r.data)
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось опубликовать заявку')
    } finally {
      setBusy(false)
    }
  }, [subject, grade, date, time, prefer, note, clientPrice, teacherPrice, initial, dealId, onCreated])

  if (!open) return null

  const lbl = 'block text-[12px] font-semibold mb-1.5'
  const inp = 'w-full h-11 px-3 rounded-xl text-[14px] outline-none'
  const inpStyle = { border: '1px solid #E4E7EC', background: 'white', color: '#101828' }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 md:p-8"
         style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }}
         onClick={onClose}>
      <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl" onClick={e => e.stopPropagation()}>

        <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #E4E7EC' }}>
          <div>
            <div className="text-[17px] font-bold" style={{ color: '#101828' }}>Отдать на биржу</div>
            <div className="text-[12px] mt-0.5" style={{ color: '#667085' }}>{clientLabel}</div>
          </div>
          <button onClick={onClose} className="text-[13px] px-3 py-1.5 rounded-lg"
                  style={{ color: '#667085' }}>Закрыть</button>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <div className="text-[16px] font-bold mb-2" style={{ color: '#101828' }}>
              Заявка опубликована
            </div>
            <div className="text-[14px] mb-1" style={{ color: '#475467' }}>
              {done.pushed
                ? `Уведомление получили ${done.notified} преподавателей.`
                : 'Заявка сохранена, но уведомления не ушли — покажите это сообщение разработчику.'}
            </div>
            {audience?.unknown > 0 && (
              <div className="text-[13px]" style={{ color: '#667085' }}>
                Ещё {audience.unknown} получат её по очереди в течение дня —
                это те, у кого предмет в базе не указан.
              </div>
            )}
            <div className="text-[13px] mt-3" style={{ color: '#667085' }}>
              Отклики появятся во вкладке «Биржа». Заявка висит сутки.
            </div>
            <button onClick={onClose}
              className="mt-5 h-11 px-6 rounded-xl font-semibold text-[14px] text-white"
              style={{ background: '#0284C7' }}>Понятно</button>
          </div>
        ) : (
          <div className="grid md:grid-cols-[1fr_320px] gap-6 p-6">

            {/* ── левая колонка: форма ── */}
            <div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl} style={{ color: '#344054' }}>Предмет</label>
                  <select className={inp} style={inpStyle} value={subject} onChange={e => setSubject(e.target.value)}>
                    <option value="">— выберите —</option>
                    {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl} style={{ color: '#344054' }}>Класс</label>
                  <select className={inp} style={inpStyle} value={grade} onChange={e => setGrade(e.target.value)}>
                    <option value="">— выберите —</option>
                    {GRADES.map(g => <option key={g} value={g}>{gradeLabel(g)}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl} style={{ color: '#344054' }}>Дата первого урока</label>
                  <input type="date" className={inp} style={inpStyle}
                         value={date} onChange={e => setDate(e.target.value)} />
                </div>
                <div>
                  <label className={lbl} style={{ color: '#344054' }}>Начало</label>
                  <input type="time" className={inp} style={inpStyle}
                         value={time} onChange={e => setTime(e.target.value)} />
                </div>
              </div>

              <div className="mt-3">
                <label className={lbl} style={{ color: '#344054' }}>Как ученику удобно заниматься</label>
                <input className={inp} style={inpStyle} value={prefer} maxLength={120}
                       placeholder="по вторникам и четвергам после 17:00"
                       onChange={e => setPrefer(e.target.value)} />
                <div className="text-[11px] mt-1" style={{ color: '#98A2B3' }}>
                  Одной строкой, словами клиента.
                </div>
              </div>

              <div className="mt-3">
                <label className={lbl} style={{ color: '#344054' }}>Что важно знать преподавателю</label>
                <textarea rows={3} maxLength={400} value={note} onChange={e => setNote(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl text-[14px] outline-none resize-none"
                  style={inpStyle}
                  placeholder="Готовится к ОГЭ, просел по геометрии" />
                <div className="text-[11px] mt-1" style={{ color: '#98A2B3' }}>
                  Без фамилии и телефона — контакты преподаватель не увидит.
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4">
                <div>
                  <label className={lbl} style={{ color: '#344054' }}>Клиент платит за урок</label>
                  <input type="number" min="0" className={inp} style={inpStyle}
                         value={clientPrice} onChange={e => setClientPrice(e.target.value)} />
                </div>
                <div>
                  <label className={lbl} style={{ color: '#344054' }}>Преподавателю за урок</label>
                  <input type="number" min="0" className={inp} style={inpStyle}
                         value={teacherPrice} onChange={e => setTeacherPrice(e.target.value)} />
                </div>
              </div>

              {/* Пока всё нормально — ничего не пишем: маржу звонильщику показывать
                  не надо (Антон 31.07). Красное предупреждение оставлено, иначе
                  можно опубликовать убыточную заявку. */}
              {marginBad && (
                <div className="mt-3 px-4 py-3 rounded-xl flex items-center justify-between"
                     style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}>
                  <span className="text-[13px] font-semibold" style={{ color: '#B91C1C' }}>
                    Преподавателю больше, чем платит клиент
                  </span>
                  <span className="text-[16px] font-bold" style={{ color: '#B91C1C' }}>
                    {margin} ₽
                  </span>
                </div>
              )}

              {error && (
                <div className="mt-3 px-4 py-3 rounded-xl text-[13px]"
                     style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C' }}>
                  {error}
                </div>
              )}

              <div className="flex gap-3 mt-5">
                <button onClick={submit} disabled={!canSubmit}
                  className="h-12 px-6 rounded-xl font-semibold text-[14px] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: '#0284C7' }}>
                  {busy ? 'Публикуем…' : 'Опубликовать на бирже'}
                </button>
                <button onClick={onClose}
                  className="h-12 px-5 rounded-xl font-semibold text-[14px]"
                  style={{ background: 'white', color: '#667085', border: '1px solid #E4E7EC' }}>
                  Отмена
                </button>
              </div>

              {subject && audience && (
                <div className="text-[12px] mt-3" style={{ color: '#667085' }}>
                  Получат сразу: <b style={{ color: '#101828' }}>{audience.matched}</b> преподавателей по этому предмету
                  {audience.unknown > 0 && <> · ещё {audience.unknown} по очереди в течение дня</>}
                </div>
              )}
            </div>

            {/* ── правая колонка: карточка глазами преподавателя ── */}
            <div>
              <div className="text-[11px] font-semibold uppercase mb-2" style={{ color: '#98A2B3', letterSpacing: '.04em' }}>
                Так заявку увидит преподаватель
              </div>
              <div className="rounded-2xl p-4" style={{ border: '1px solid #E4E7EC', background: '#FCFCFD' }}>
                <div className="text-[15px] font-bold" style={{ color: '#101828' }}>
                  {subject || 'Предмет'}{grade ? `, ${gradeLabel(grade)}` : ''}
                </div>
                <div className="text-[13px] mt-1.5" style={{ color: '#475467' }}>
                  {date ? `Урок ${humanDate(date)}` : 'Дата урока не выбрана'}
                  {date && time ? `, с ${time} до ${plus55(time)}` : ''}
                </div>
                {prefer && (
                  <div className="text-[13px] mt-1" style={{ color: '#475467' }}>Удобно: {prefer}</div>
                )}
                {note && (
                  <div className="text-[13px] mt-2 px-3 py-2 rounded-lg"
                       style={{ background: 'white', border: '1px solid #E4E7EC', color: '#475467' }}>
                    {note}
                  </div>
                )}
                <div className="mt-3 pt-3" style={{ borderTop: '1px solid #E4E7EC' }}>
                  <div className="text-[11px]" style={{ color: '#98A2B3' }}>Оплата за урок</div>
                  <div className="text-[22px] font-bold" style={{ color: '#101828' }}>
                    {Number(teacherPrice || 0)} ₽
                  </div>
                </div>
                <button disabled
                  className="w-full h-11 mt-3 rounded-xl font-semibold text-[14px] text-white opacity-60 cursor-default"
                  style={{ background: '#0284C7' }}>Беру</button>
              </div>
              <div className="text-[11px] mt-2" style={{ color: '#98A2B3' }}>
                Имени и телефона в карточке нет. Уроки идут на платформе, как обычно.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
